import { useId } from 'react';
import type { AssistantToolInfo } from '@cumpli/shared';
import { cn } from '../../ui';
import { toolIcon } from '../tool-icons';

/**
 * `list_capabilities` — the RBAC-filtered capability catalog rendered INLINE in a
 * chat turn as grouped Consultas / Acciones cards (icon + label + one-line
 * description). It's the compact sibling of the /agente empty-state
 * <ToolExplorer>: same card + icon idiom, but denser and narrower so it sits
 * cleanly inside an answer bubble. The model's one-line intro ("Esto es lo que
 * puedo hacer:") renders above via the answer markdown, so the card carries no
 * heading of its own.
 *
 * Clicking a card SEEDS the composer with the tool's `prompt` (never auto-sends);
 * write ('accion') tools then run through the normal propose → confirm ActionCard
 * flow. When no seed handler is wired (a surface that can't fill its composer)
 * the cards render as static, non-interactive rows so nothing crashes.
 *
 * `variant` controls density: the narrow drawer / mobile ⌘K stays single-column;
 * the wide /agente page goes 2-up on sm+. Content is fully synchronous (no async
 * inside the card), so heights are stable and the thread's stick-to-bottom
 * ResizeObserver keeps the view pinned without layout thrash.
 */

function CapabilityCard({
  tool,
  onPick,
}: {
  tool: AssistantToolInfo;
  onPick?: ((prompt: string) => void) | undefined;
}) {
  const Icon = toolIcon(tool.name);
  const body = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/[0.06] text-primary transition-colors group-hover:bg-primary/10">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0">
        <span className="block font-serif text-[13px] font-medium leading-snug text-ink">
          {tool.label}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-ink-soft">{tool.description}</span>
      </span>
    </>
  );
  const base = 'flex items-start gap-2.5 rounded-xl border border-line bg-panel p-2.5 text-left';
  // Non-interactive fallback: a surface with no composer to seed renders static
  // rows (no button affordance, no hover/focus ring) instead of a dead click.
  if (!onPick) {
    return <div className={base}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onPick(tool.prompt)}
      className={cn(
        base,
        'group transition-all hover:border-primary/40 hover:shadow-[0_10px_30px_-18px_rgba(138,46,60,.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
      )}
    >
      {body}
    </button>
  );
}

function CapabilitySection({
  title,
  tools,
  cols,
  onPick,
}: {
  title: string;
  tools: AssistantToolInfo[];
  cols: string;
  onPick?: ((prompt: string) => void) | undefined;
}) {
  const headingId = useId();
  if (tools.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex items-center gap-2 pl-0.5">
        <span
          id={headingId}
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint"
        >
          {title}
        </span>
        <span className="grid h-4 min-w-4 place-items-center rounded-full bg-primary/10 px-1 font-mono text-[9px] font-bold text-primary-dark">
          {tools.length}
        </span>
      </div>
      <div className={cn('grid gap-2 [&>*]:min-w-0', cols)}>
        {tools.map((tool) => (
          <CapabilityCard key={tool.name} tool={tool} onPick={onPick} />
        ))}
      </div>
    </section>
  );
}

export function CapabilitiesCard({
  tools,
  variant = 'page',
  onPick,
}: {
  tools: AssistantToolInfo[];
  /** 'drawer' → narrow single column; 'page' → 2-up on sm+. */
  variant?: 'drawer' | 'page' | undefined;
  /** Seed the composer with the picked capability's `prompt` (no auto-send). */
  onPick?: ((prompt: string) => void) | undefined;
}) {
  const consultas = tools.filter((t) => t.category === 'consulta');
  const acciones = tools.filter((t) => t.category === 'accion');
  // Narrow surfaces (drawer, mobile ⌘K) stay single-column; the wide page goes 2-up.
  const cols = variant === 'drawer' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2';
  return (
    <div className="space-y-4">
      <CapabilitySection title="Consultas" tools={consultas} cols={cols} onPick={onPick} />
      <CapabilitySection title="Acciones" tools={acciones} cols={cols} onPick={onPick} />
    </div>
  );
}
